/**
 * A notification that ARRIVES while you are looking at something else.
 *
 * The bell has always moved a badge and nothing more, so a new announcement,
 * mention or helpdesk reply was only discovered by someone happening to glance
 * at the corner of the toolbar. This surfaces it.
 *
 * ⚠️ **Not an SSE stream, deliberately — for now.** gunicorn here is
 * `worker_class = 'sync'` with `workers = 3`, and an SSE connection holds a
 * worker for its entire lifetime. The FOURTH person to open the intranet would
 * hang the whole app for everybody — no pages, no API. Making notifications a
 * real stream is a worker-class change (gevent/eventlet + monkey-patching,
 * which touches every blocking DB call in the app), and that deserves its own
 * change rather than riding along with a popup. Until then this is driven by
 * the bell's existing poll, tightened to 20s.
 *
 * ⚠️ **Never fires on the first poll.** The baseline is taken silently, or
 * every page load would replay every unread notification you already had — the
 * fastest way to make people disable the feature.
 */
import { getIcon } from '@isg/ui-kit';
import { t } from '../i18n/index.js';

const AUTO_DISMISS_MS = 9000;
const MAX_VISIBLE = 3;

let stack = null;

function ensureStack() {
  if (stack && stack.isConnected) return stack;
  stack = document.createElement('div');
  stack.className = 'notif-popup-stack';
  stack.setAttribute('role', 'status');
  stack.setAttribute('aria-live', 'polite');
  stack.style.cssText = [
    'position:fixed', 'right:16px', 'bottom:16px',
    'display:flex', 'flex-direction:column', 'gap:10px',
    // Above the app shell, below a modal — a notification must never cover a
    // dialog someone is mid-way through answering.
    'z-index:900',
    'max-width:min(92vw,380px)', 'pointer-events:none',
  ].join(';');
  document.body.appendChild(stack);
  return stack;
}

function escapeHtml(str) {
  return String(str == null ? '' : str).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

/**
 * Show one popup.
 *
 * ⚠️ Colours are LITERAL, not tokens. The ui-kit grey scale INVERTS under
 * `[data-theme="dark"]`, so a token pair here is unreadable in exactly one
 * theme — the same trap the org-chart tooltip, the RSVP chips and the
 * dirty-guard hint each hit.
 */
export function showNotificationPopup(notif, onOpen) {
  const host = ensureStack();

  // Oldest first out, so the newest is always the one nearest the corner.
  while (host.children.length >= MAX_VISIBLE) host.firstChild.remove();

  const card = document.createElement('div');
  card.style.cssText = [
    'pointer-events:auto', 'cursor:pointer',
    'background:#171e26', 'color:#f1f2f4',
    'border-radius:12px', 'padding:12px 14px',
    // i18n-ignore: CSS declaration, not text
    'box-shadow:0 8px 28px rgba(0,0,0,.38)',
    'display:flex', 'gap:10px', 'align-items:flex-start',
    'opacity:0', 'transform:translateY(8px)',
    'transition:opacity .22s ease, transform .22s ease',
  ].join(';');

  const body = (notif.body || '').trim();
  card.innerHTML = `
    <span style="flex-shrink:0;opacity:.9;margin-top:1px;">${getIcon('bell', 16)}</span>
    <span style="min-width:0;flex:1;">
      <span style="display:block;font-weight:600;font-size:13px;line-height:1.35;">
        ${escapeHtml(notif.title || t('notifications.new'))}
      </span>
      ${body ? `<span style="display:block;font-size:12px;line-height:1.45;opacity:.82;
                  margin-top:2px;overflow:hidden;display:-webkit-box;
                  -webkit-line-clamp:2;-webkit-box-orient:vertical;">
                  ${escapeHtml(body)}</span>` : ''}
    </span>
    <button aria-label="${t('common.close')}" style="flex-shrink:0;background:none;border:none;
            color:inherit;opacity:.6;cursor:pointer;font-size:15px;line-height:1;padding:0 2px;">&times;</button>
  `;

  let timer = null;
  const dismiss = () => {
    clearTimeout(timer);
    card.style.opacity = '0';
    card.style.transform = 'translateY(8px)';
    setTimeout(() => card.remove(), 240);
  };

  card.querySelector('button').addEventListener('click', (e) => {
    e.stopPropagation();          // closing is not opening
    dismiss();
  });
  card.addEventListener('click', () => {
    dismiss();
    if (typeof onOpen === 'function') onOpen(notif);
  });
  // Hovering means someone is reading it — don't yank it away mid-sentence.
  card.addEventListener('mouseenter', () => clearTimeout(timer));
  card.addEventListener('mouseleave', () => { timer = setTimeout(dismiss, 2500); });

  host.appendChild(card);
  requestAnimationFrame(() => {
    card.style.opacity = '1';
    card.style.transform = 'translateY(0)';
  });
  timer = setTimeout(dismiss, AUTO_DISMISS_MS);
  return dismiss;
}
